import { useCallback, useState } from 'react'

import { apiService } from '../../services/api'
import { sectionTagsAnnotationId } from '../../utils/sectionTagsAnnotationId'

interface EntryLike {
  chapter: { filename: string }
  headingBlockId: string
  tagIds: string[]
}

/**
 * Shared hook for editing the canonical section-tags annotation on a `^` heading.
 * Maintains optimistic overrides keyed by chapterSectionKey, persists via
 * apiService.updateAnnotation, and rolls back on failure.
 */
export function useSectionTagsEditing({
  bookId,
  entryByKey,
  onPersisted,
}: {
  bookId: string | undefined
  entryByKey: Map<string, EntryLike>
  onPersisted?: () => void
}) {
  const [override, setOverride] = useState<Record<string, string[]>>({})

  const effectiveTagIds = useCallback(
    (sectionKey: string): string[] => {
      if (sectionKey in override) {
        return override[sectionKey]
      }
      return entryByKey.get(sectionKey)?.tagIds ?? []
    },
    [entryByKey, override],
  )

  const changeTagIds = useCallback(
    async (sectionKey: string, next: string[]) => {
      const entry = entryByKey.get(sectionKey)
      if (!entry || !bookId) {
        return
      }
      const prev = override[sectionKey] ?? entry.tagIds
      setOverride((curr) => ({ ...curr, [sectionKey]: next }))
      try {
        await apiService.updateAnnotation(bookId, entry.chapter.filename, {
          id: sectionTagsAnnotationId(entry.headingBlockId),
          tags: next,
          anchor: { type: 'block', blockId: entry.headingBlockId },
          createdAt: new Date(),
        })
        onPersisted?.()
      } catch (err) {
        setOverride((curr) => ({ ...curr, [sectionKey]: prev }))
        throw err
      }
    },
    [bookId, entryByKey, override, onPersisted],
  )

  const resetOverrides = useCallback(() => {
    setOverride({})
  }, [])

  return { effectiveTagIds, changeTagIds, resetOverrides }
}
